import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Layers, Plus } from 'lucide-react';
import { api } from '@/lib/api/client';
import type { GiftCardBatch, PaginatedResponse } from '@/lib/api/types';
import { humanize, money, num, smartDate } from '@/lib/format';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { DataTable, type Column } from '@/ui/DataTable';
import { Pagination, SubNav, SubNavItem } from '@/ui/Filters';
import { Page, PageHeader, Toolbar } from '@/ui/Panel';

const PAGE_SIZE = 25;

type BatchesSearch = { page: number };

export const Route = createFileRoute('/_app/gift-cards_/batches')({
  staticData: { crumb: 'Batches' },
  validateSearch: (s: Record<string, unknown>): BatchesSearch => ({
    page: Number(s.page) > 0 ? Math.floor(Number(s.page)) : 1,
  }),
  component: BatchesScreen,
});

export function chargeMethodTone(method: string | null | undefined) {
  if (method === 'house_account') return 'warn' as const;
  if (method === 'invoice') return 'info' as const;
  return 'neutral' as const;
}

function BatchesScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const list = useQuery({
    queryKey: ['gift-card-batches', 'list', search.page],
    queryFn: () =>
      api.get<PaginatedResponse<GiftCardBatch>>('/gift-card-batches', {
        params: { page: search.page, pageSize: PAGE_SIZE },
      }),
    placeholderData: (prev) => prev,
  });

  const rows = list.data?.data;
  const total = list.data?.total ?? 0;

  const columns: Array<Column<GiftCardBatch>> = [
    {
      id: 'name',
      header: 'Batch',
      cell: (row) => (
        <div className="min-w-0 leading-tight">
          <div className="text-ink truncate">{row.name}</div>
          {row.notes ? (
            <div className="text-2xs text-ink-faint truncate">{row.notes}</div>
          ) : null}
        </div>
      ),
    },
    {
      id: 'quantity',
      header: 'Cards',
      width: '5rem',
      align: 'right',
      mono: true,
      cell: (row) => num(row.quantity),
    },
    {
      id: 'faceValue',
      header: 'Face value',
      width: '7rem',
      align: 'right',
      mono: true,
      cell: (row) => money(row.faceValueCents),
    },
    {
      id: 'totalFaceValue',
      header: 'Total value',
      width: '7.5rem',
      align: 'right',
      mono: true,
      hideBelow: 'md',
      cell: (row) => money(row.totalFaceValueCents),
    },
    {
      id: 'discount',
      header: 'Discount',
      width: '6.5rem',
      align: 'right',
      mono: true,
      hideBelow: 'lg',
      cell: (row) =>
        row.discountCents ? (
          <span className="text-positive">−{money(row.discountCents)}</span>
        ) : (
          <span className="text-ink-faint">—</span>
        ),
    },
    {
      id: 'charged',
      header: 'Charged',
      width: '7rem',
      align: 'right',
      mono: true,
      cell: (row) => <span className="text-ink">{money(row.chargedAmountCents)}</span>,
    },
    {
      id: 'method',
      header: 'Method',
      width: '8rem',
      cell: (row) => (
        <Badge tone={chargeMethodTone(row.chargeMethod)}>
          {row.chargeMethod ? humanize(row.chargeMethod) : 'No charge'}
        </Badge>
      ),
    },
    {
      id: 'created',
      header: 'Created',
      width: '6rem',
      mono: true,
      hideBelow: 'md',
      cell: (row) => <span className="text-ink-faint">{smartDate(row.createdAt)}</span>,
    },
  ];

  return (
    <Page>
      <PageHeader
        eyebrow="Gift cards"
        title="Batches"
        subtitle="Bulk runs — printing, WhatsApp captions and how each was paid for"
        actions={
          <Button
            variant="primary"
            size="sm"
            icon={<Plus className="size-3.5" />}
            onClick={() => void navigate({ to: '/gift-cards/batches/new' })}
          >
            New batch
          </Button>
        }
        tabs={
          <SubNav>
            <SubNavItem
              active={false}
              onClick={() =>
                void navigate({
                  to: '/gift-cards',
                  search: {
                    q: '',
                    status: 'all',
                    sort: 'created',
                    order: 'desc',
                    page: 1,
                  },
                })
              }
            >
              Cards
            </SubNavItem>
            <SubNavItem active onClick={() => undefined}>
              Batches
            </SubNavItem>
          </SubNav>
        }
      />
      <Toolbar>
        <span className="text-2xs text-ink-faint">Newest first</span>
        <div className="ml-auto">
          <Pagination
            page={search.page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={(page) => void navigate({ search: () => ({ page }) })}
          />
        </div>
      </Toolbar>
      <div className="min-h-0 flex-1">
        <DataTable
          density="comfortable"
          rows={rows}
          columns={columns}
          rowKey={(row) => row.id}
          loading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
          onRowOpen={(row) =>
            void navigate({
              to: '/gift-cards/batches/$id',
              params: { id: String(row.id) },
            })
          }
          empty={{
            icon: <Layers />,
            title: 'No batches yet',
            description:
              'A batch generates many cards at once, with one shared design and one charge.',
          }}
          footer={<span>{num(total)} batches</span>}
        />
      </div>
    </Page>
  );
}
